// Complaint traceability (rule 36): the case's batch traced back through its picking list to every
// ingredient batch, each bought batch's goods-in record and any D365 NCR on it, with Claude's summary.
//
// LAID OUT FOR "WHAT DO I LOOK AT" (UI/UX pass, 2 Nov 2026): the trace was 88 flat lines, 14,000 px on a
// phone, 70 of them the same "on paper" sentence. Now:
//   - one card, collapsed to a single line of counts until opened, so the case's own work stays in reach;
//   - Claude's saved summary shows on page load (one read), not only after a new trace;
//   - flagged lines first; in-house bases and premixes NESTED under the line that used them, so the path to
//     a flag reads as a path; bought batches received before the cutover folded into one row per list;
//   - one footnote says how fresh each source is, amber when the NCR data is over three days old.
// Read on demand: the walk reads INVENTORYV2 and every PO line. Records, not a cause: flags are shown,
// nothing is scored; QA's finding is the record.
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { api } from '../../lib/api';
import { officeFocus, serverMessage } from '../../shells/OfficeShell';
import { day } from './complaintsModel';

interface GoodsIn {
  state: 'IN_APP' | 'ON_PAPER' | 'NOT_IN_SNAPSHOT' | 'NOT_KNOWN';
  reason: string;
  receiptId: number | null;
  po: string | null;
  line: number | null;
  supplier: string | null;
  receivedDate: string | null;
  approved: boolean | null;
  certificate: { verdict: string | null; decision: string | null; automatic: boolean } | null;
  dock: { vehicle: string | null; goods: string | null; summary: string | null };
  flags: string[];
  appUrl: string | null;
}

interface Ncr {
  ncr: string;
  item: string;
  batch: string;
  type: string | null;
  problem: string | null;
  closed: boolean;
  date: string | null;
}

interface TraceInput {
  order: string;
  item: string;
  itemName: string | null;
  batch: string | null;
  qty: number;
  unit: string | null;
  kind: 'MADE_HERE' | 'BOUGHT' | 'NO_BATCH' | 'ORIGIN_NOT_FOUND';
  madeBy: string[];
  origins: Array<{ type: string; id: string; date: string | null }>;
  goodsIn: GoodsIn | null;
  holds: number[];
  ncrs?: Ncr[];
}

interface Trace {
  item: string;
  batch: string;
  asOf: string | null;
  depth: number;
  notWalked: number;
  levels: Array<{ depth: number; orders: string[]; inputs: TraceInput[] }>;
  sentence: string;
  fgNcrs?: Ncr[];
  ncrsUnknown?: string | null;
  ncrsAsOf?: string | null;
}

interface Summary {
  summary: string;
  lookFirst: Array<{ item: string; batch: string; why: string }>;
  gaps: string[];
  model: string;
  byUpn: string | null;
  at: string;
}

const card = 'flex flex-col gap-(--s3) rounded-md border border-(--border) bg-(--card) p-(--s4)';
const pill = (tone: 'ok' | 'warn' | 'quiet') =>
  `rounded-full px-2 py-0.5 text-[11px] font-semibold ${tone === 'ok' ? 'bg-(--in-bg) text-(--in-fg)' : tone === 'warn' ? 'bg-(--action-bg) text-(--action-fg)' : 'bg-(--subtle) text-(--muted)'}`;
const qty = (n: number, unit: string | null) => `${n >= 1000 ? Math.round(n).toLocaleString('en-AU') : Math.round(n * 100) / 100}${unit ? ` ${unit}` : ''}`;
const ncrText = (n: Ncr) => `NCR ${n.ncr}${n.problem ? ` ${n.problem}` : ''} (${n.closed ? 'closed' : 'open'}${n.date ? `, ${day(n.date)}` : ''})`;
const STALE_DAYS = 3;

/* ── The shape of the tree ────────────────────────────── */

type Node = TraceInput & { children: Node[] };

/** Each in-house batch's own inputs hung under it: level d+1 lines whose order made it. */
export function buildTree(levels: Trace['levels']): Node[] {
  const at = (d: number): Node[] => (levels[d]?.inputs ?? []).map((i) => ({ ...i, children: [] }));
  const lv = levels.map((_, d) => at(d));
  for (let d = lv.length - 2; d >= 0; d--) {
    for (const n of lv[d]) if (n.kind === 'MADE_HERE') n.children = lv[d + 1].filter((c) => n.madeBy.includes(c.order));
  }
  return lv[0] ?? [];
}

/** The flags of one line itself (not its children): what QA would look at. */
export function ownFlags(i: TraceInput): string[] {
  const f = [...(i.ncrs ?? []).map(ncrText), ...(i.goodsIn?.flags ?? []), ...i.holds.map((h) => `QC hold #${h}`)];
  if (i.kind === 'ORIGIN_NOT_FOUND') f.push('origin not found');
  if (i.goodsIn && (i.goodsIn.state === 'NOT_KNOWN' || i.goodsIn.state === 'NOT_IN_SNAPSHOT')) f.push('goods-in not known');
  return f;
}

/** Flagged lines in this line and everything under it. */
export function flaggedWithin(n: Node): number {
  return (ownFlags(n).length ? 1 : 0) + n.children.reduce((a, c) => a + flaggedWithin(c), 0);
}

const isPaper = (n: Node) => n.kind === 'BOUGHT' && n.goodsIn?.state === 'ON_PAPER' && ownFlags(n).length === 0;
const isPackaging = (n: Node) => n.kind === 'NO_BATCH' && ownFlags(n).length === 0;

/** The order a list is read in: flagged (itself or under it) first, then in-house, then in the app. */
export function ordered(list: readonly Node[]): Node[] {
  const rank = (n: Node) => (flaggedWithin(n) > 0 ? 0 : n.kind === 'MADE_HERE' ? 1 : 2);
  return [...list].sort((a, b) => rank(a) - rank(b) || flaggedWithin(b) - flaggedWithin(a));
}

/* ── Lines ────────────────────────────────────────────── */

function GoodsInLine({ g }: { g: GoodsIn }) {
  if (g.state !== 'IN_APP') return <span className={g.state === 'ON_PAPER' ? 'text-(--muted)' : 'text-(--warn)'}>{g.reason}</span>;
  const cert = g.certificate
    ? `certificate ${g.certificate.verdict ?? 'not judged'}${g.certificate.decision ? `, ${g.certificate.decision.toLowerCase().replace(/_/g, ' ')}${g.certificate.automatic ? ' (automatic)' : ''}` : ', no decision'}`
    : 'no certificate';
  return (
    <span className="flex flex-col">
      <span>
        {g.supplier ?? 'supplier not known'} ·{' '}
        {g.receiptId ? (
          <Link to={`/office/goods-in/${g.receiptId}`} className={`font-semibold underline ${officeFocus}`}>
            {g.po}
            {g.line && g.line !== 1 ? ` / ${g.line}` : ''}
          </Link>
        ) : (
          g.po
        )}
        {g.receivedDate ? ` · received ${day(g.receivedDate)}` : ''}
      </span>
      <span className="text-[12px] text-(--muted)">
        {g.approved === true ? 'batch approved' : g.approved === false ? 'batch not approved' : 'approval not known'} · {cert} · vehicle{' '}
        {g.dock.vehicle?.toLowerCase() ?? 'not recorded'} · goods {g.dock.goods?.toLowerCase() ?? 'not recorded'}
      </span>
    </span>
  );
}

function Line({ n }: { n: Node }) {
  const flags = ownFlags(n);
  const inside = n.children.reduce((a, c) => a + flaggedWithin(c), 0);
  const head = (
    <span className="flex flex-wrap items-baseline gap-x-(--s2) gap-y-1">
      <span className="font-semibold">{n.itemName ?? n.item}</span>
      <span className="text-[12px] text-(--muted) tabular-nums">
        {n.item}
        {n.batch ? ` · batch ${n.batch}` : ''} · {qty(n.qty, n.unit)}
      </span>
      {n.kind === 'MADE_HERE' ? <span className={pill('quiet')}>made here · {n.madeBy.join(', ')}</span> : null}
      {inside > 0 ? <span className={pill('warn')}>{inside} flagged inside</span> : null}
    </span>
  );
  const body = (
    <>
      {n.kind === 'BOUGHT' && n.goodsIn ? <GoodsInLine g={n.goodsIn} /> : null}
      {n.kind === 'ORIGIN_NOT_FOUND' ? <span className="text-(--warn)">Where this batch came from is not in INVENTORYV2.</span> : null}
      {flags.length ? (
        <span className="flex flex-wrap gap-(--s1)">
          {flags.map((f) => (
            <span key={f} className={pill('warn')}>
              {f}
            </span>
          ))}
        </span>
      ) : null}
    </>
  );
  if (n.kind !== 'MADE_HERE' || n.children.length === 0) {
    return (
      <li className="flex flex-col gap-1 border-b border-(--border) py-(--s2) last:border-b-0">
        {head}
        {body}
      </li>
    );
  }
  // An in-house base or premix: its own inputs nested, open when something under it is flagged.
  return (
    <li className="border-b border-(--border) py-(--s2) last:border-b-0">
      <details open={inside > 0}>
        <summary className={`flex min-h-[44px] cursor-pointer flex-col justify-center gap-1 ${officeFocus}`}>
          {head}
          <span className="text-[12px] text-(--muted)">{n.children.length} inputs on its order</span>
        </summary>
        {body}
        <div className="mt-(--s2) border-l-2 border-(--border) pl-(--s3)">
          <List nodes={n.children} />
        </div>
      </details>
    </li>
  );
}

/** One list of lines: flagged first; on-paper batches and packaging folded into a row each. */
function List({ nodes }: { nodes: readonly Node[] }) {
  const shown = ordered(nodes.filter((n) => !isPaper(n) && !isPackaging(n)));
  const paper = nodes.filter(isPaper);
  const packaging = nodes.filter(isPackaging);
  return (
    <ul className="m-0 flex list-none flex-col p-0 text-(length:--fs-sm)">
      {shown.map((n) => (
        <Line key={`${n.order}-${n.item}-${n.batch ?? 'none'}`} n={n} />
      ))}
      {paper.length ? (
        <li className="py-(--s2)">
          <details>
            <summary className={`flex min-h-[44px] cursor-pointer items-center text-(--muted) ${officeFocus}`}>
              {paper.length} bought batch{paper.length === 1 ? '' : 'es'} received before 1 Oct 2026: certificates and checks on paper
            </summary>
            <ul className="m-0 mt-(--s1) flex list-none flex-col gap-1 p-0 text-[12px]">
              {paper.map((n) => (
                <li key={`${n.item}-${n.batch}`} className="tabular-nums">
                  <span className="font-semibold">{n.itemName ?? n.item}</span> · {n.batch} · {qty(n.qty, n.unit)}
                  {n.goodsIn?.po ? ` · ${n.goodsIn.po}` : ''}
                </li>
              ))}
            </ul>
          </details>
        </li>
      ) : null}
      {packaging.length ? (
        <li className="py-(--s2) text-[12px] text-(--muted)">
          {packaging.map((n) => n.itemName ?? n.item).join(', ')}: packaging without a batch, traced to the item only.
        </li>
      ) : null}
    </ul>
  );
}

/* ── Claude's summary ─────────────────────────────────── */

function SummaryPanel({ caseId, summary, canSummarise, onDone }: { caseId: number; summary: Summary | null; canSummarise: boolean; onDone: (s: Summary) => void }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const ask = async () => {
    setBusy(true);
    setError(null);
    try {
      const r = await api.post<{ summary: Summary }>(`/complaints/${caseId}/trace/summary`, {});
      onDone(r.summary);
    } catch (e) {
      setError(serverMessage(e));
    } finally {
      setBusy(false);
    }
  };
  if (!summary && !canSummarise) return null;
  return (
    <div className="flex flex-col gap-(--s2) rounded-md border border-(--border) bg-(--subtle) p-(--s3)">
      <div className="flex flex-wrap items-baseline justify-between gap-(--s2)">
        <span className="text-[12px] font-semibold uppercase tracking-[0.06em] text-(--muted)">Claude's summary · a suggestion, not the finding</span>
        {summary ? <span className="text-[11px] text-(--muted)">asked by {summary.byUpn ?? 'someone'}, {day(summary.at.slice(0, 10))}</span> : null}
      </div>
      {summary ? (
        <>
          <p className="m-0 text-(length:--fs-sm) leading-relaxed">{summary.summary}</p>
          {summary.lookFirst.length ? (
            <div className="flex flex-col gap-1">
              <span className="text-[12px] font-semibold">Worth checking first</span>
              <ul className="m-0 flex list-disc flex-col gap-1 pl-(--s4) text-(length:--fs-sm)">
                {summary.lookFirst.map((x) => (
                  <li key={`${x.item}-${x.batch}`}>
                    <span className="font-semibold tabular-nums">
                      {x.item} · {x.batch}
                    </span>
                    : {x.why}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
          {summary.gaps.length ? (
            <details>
              <summary className={`min-h-[44px] cursor-pointer text-[12px] text-(--muted) ${officeFocus}`}>What the trace cannot show ({summary.gaps.length})</summary>
              <ul className="m-0 flex list-disc flex-col gap-1 pl-(--s4) text-[12px] text-(--muted)">
                {summary.gaps.map((g) => (
                  <li key={g}>{g}</li>
                ))}
              </ul>
            </details>
          ) : null}
        </>
      ) : (
        <p className="m-0 text-[12px] text-(--muted)">Claude reads the trace and points at the records worth checking first. It never names a cause.</p>
      )}
      {canSummarise ? (
        <div className="flex flex-wrap items-center gap-(--s2)">
          <Button type="button" size="sm" variant="outline" className="min-h-[44px] sm:min-h-0" disabled={busy} onClick={() => void ask()}>
            {busy ? 'Claude is reading the trace…' : summary ? 'Summarise again' : 'Summarise with Claude'}
          </Button>
          {error ? (
            <span role="alert" className="text-(length:--fs-sm) font-semibold text-(--warn)">
              {error}
            </span>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

/* ── The card ─────────────────────────────────────────── */

/** The flagged lines, deepest first by flag count, for the closed card: what Jacob decides on without a tap
 *  (UI/UX pass, 3 Nov 2026: the 4 flagged lines of CMP-0060 - an almond batch with a spec NCR under a "no
 *  almonds" complaint - sat behind "Show trace" and 2,400 px). */
export function flaggedLines(t: Trace, max = 5): Array<{ key: string; name: string; batch: string | null; flags: string[] }> {
  return t.levels
    .flatMap((l) => l.inputs)
    .map((i) => ({ key: `${i.order}-${i.item}-${i.batch ?? 'none'}`, name: i.itemName ?? i.item, batch: i.batch, flags: ownFlags(i) }))
    .filter((x) => x.flags.length > 0)
    .sort((a, b) => b.flags.length - a.flags.length)
    .slice(0, max);
}

/** "2 flagged · 3 D365 NCRs · 74 on paper · 85 lines": the card's closed line. */
export function countsLine(t: Trace): string {
  const all = t.levels.flatMap((l) => l.inputs);
  const flagged = all.filter((i) => ownFlags(i).length > 0).length;
  const ncrs = new Set([...(t.fgNcrs ?? []), ...all.flatMap((i) => i.ncrs ?? [])].map((n) => n.ncr)).size;
  const paper = all.filter((i) => i.goodsIn?.state === 'ON_PAPER').length;
  return [
    `${flagged} flagged`,
    t.ncrsUnknown ? 'D365 NCRs not known' : `${ncrs} D365 NCR${ncrs === 1 ? '' : 's'}`,
    `${paper} on paper`,
    `${all.length} input lines over ${t.depth} level${t.depth === 1 ? '' : 's'}`,
  ].join(' · ');
}

function Freshness({ t }: { t: Trace }) {
  const ncrAge = t.ncrsAsOf ? (Date.now() - Date.parse(`${t.ncrsAsOf.replace(' ', 'T')}Z`)) / 86_400_000 : null;
  const stale = ncrAge !== null && ncrAge > STALE_DAYS;
  return (
    <p className="m-0 text-[11px] text-(--muted)">
      Data as of: what went in, INVENTORYV2 {t.asOf ? `${t.asOf.slice(0, 16)} UTC` : 'not known'} ·{' '}
      <span className={stale ? 'font-semibold text-(--warn)' : ''}>
        D365 NCRs {t.ncrsAsOf ? `${t.ncrsAsOf.slice(0, 16)} UTC${stale ? ` (${Math.floor(ncrAge!)} days old: a newer NCR may be missing)` : ''}` : 'not known'}
      </span>{' '}
      · goods-in, this app now. Only NCRs raised with a batch are found. Records, not a cause: QA's finding is the record.
    </p>
  );
}

export function ComplaintTrace({ caseId }: { caseId: number }) {
  const [trace, setTrace] = useState<Trace | null>(null);
  const [open, setOpen] = useState(false);
  const [summary, setSummary] = useState<Summary | null>(null);
  const [canSummarise, setCanSummarise] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [traced, setTraced] = useState<{ at: string; by: string | null } | null>(null);

  // On page load: the stored trace (the mirror traces a case once its batch is known, 051) and the saved
  // summary - one read, no 40-second walk and no model.
  useEffect(() => {
    let live = true;
    api
      .get<{ trace: Trace | null; tracedAt: string | null; tracedBy: string | null; summary: Summary | null; canSummarise: boolean }>(`/complaints/${caseId}/trace?stored=1`)
      .then((r) => {
        if (!live) return;
        if (r.trace) {
          setTrace(r.trace);
          setTraced(r.tracedAt ? { at: r.tracedAt, by: r.tracedBy } : null);
        }
        setSummary(r.summary);
        setCanSummarise(r.canSummarise === true);
      })
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, [caseId]);

  const run = async () => {
    setBusy(true);
    setError(null);
    try {
      const r = await api.get<{ trace: Trace; tracedAt?: string; tracedBy?: string; summary?: Summary | null; canSummarise?: boolean }>(`/complaints/${caseId}/trace`);
      setTrace(r.trace);
      setTraced(r.tracedAt ? { at: r.tracedAt, by: r.tracedBy ?? null } : null);
      setOpen(true);
      if (r.summary) setSummary(r.summary);
      setCanSummarise(r.canSummarise === true);
    } catch (e) {
      setError(serverMessage(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className={card} aria-label="Trace">
      <div className="flex flex-wrap items-center justify-between gap-(--s2)">
        <div className="min-w-0">
          <h2 className="m-0 text-(length:--fs-sm) font-semibold">Trace: what went into this batch</h2>
          <p className="m-0 text-[12px] text-(--muted)">{trace ? countsLine(trace) : 'Picking list, in-house bases, every bought batch with its goods-in record and D365 NCRs.'}</p>
          {traced ? (
            <p className="m-0 text-[11px] text-(--muted)">
              Traced {new Date(traced.at).toLocaleString('en-AU', { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' })}
              {traced.by ? ` by ${traced.by}` : ' automatically'}. Records may have changed since: Trace again reads them fresh.
            </p>
          ) : null}
        </div>
        <div className="flex flex-wrap gap-(--s2)">
          {trace ? (
            <Button type="button" variant="outline" size="sm" className="min-h-[44px] sm:min-h-0" aria-expanded={open} onClick={() => setOpen((o) => !o)}>
              {open ? 'Hide full trace' : 'Show full trace'}
            </Button>
          ) : null}
          {/* "Trace again" only once the trace is open: on a phone the closed card is the decision, not the controls. */}
          {!trace || open ? (
            <Button type="button" variant="outline" size="sm" className="min-h-[44px] sm:min-h-0" disabled={busy} onClick={() => void run()}>
              {busy ? 'Tracing… (about 40 s)' : trace ? 'Trace again' : 'Trace this batch'}
            </Button>
          ) : null}
        </div>
      </div>
      {trace && !open ? (
        (() => {
          const fl = flaggedLines(trace);
          const total = trace.levels.flatMap((l) => l.inputs).filter((i) => ownFlags(i).length > 0).length;
          return fl.length ? (
            <ul className="m-0 flex list-none flex-col gap-(--s2) p-0 text-(length:--fs-sm)" aria-label="Flagged in the trace">
              {fl.map((x) => (
                <li key={x.key} className="flex flex-col gap-1">
                  <span>
                    <span className="font-semibold">{x.name}</span>
                    {x.batch ? <span className="text-[12px] text-(--muted) tabular-nums"> · batch {x.batch}</span> : null}
                  </span>
                  <span className="flex flex-wrap gap-(--s1)">
                    {x.flags.map((f) => (
                      <span key={f} className={pill('warn')}>
                        {f}
                      </span>
                    ))}
                  </span>
                </li>
              ))}
              {total > fl.length ? <li className="text-[12px] text-(--muted)">and {total - fl.length} more in the full trace.</li> : null}
            </ul>
          ) : (
            <p className="m-0 text-(length:--fs-sm) text-(--muted)">Nothing flagged on any batch in the trace.</p>
          );
        })()
      ) : null}
      {error ? (
        <p role="alert" className="m-0 text-(length:--fs-sm) font-semibold text-(--warn)">
          Could not trace: {error} This is not the same as "nothing found".
        </p>
      ) : null}

      <SummaryPanel caseId={caseId} summary={summary} canSummarise={canSummarise} onDone={setSummary} />

      {trace && open ? (
        <>
          <p className="m-0 text-(length:--fs-sm)">
            <span className="font-semibold">D365 NCRs on batch {trace.batch}: </span>
            {trace.ncrsUnknown ? (
              <span className="text-(--warn)">not known ({trace.ncrsUnknown})</span>
            ) : trace.fgNcrs?.length ? (
              <span className="font-semibold text-(--warn)">{trace.fgNcrs.map(ncrText).join('; ')}</span>
            ) : (
              'none raised with this batch'
            )}
          </p>
          <div className="flex flex-col gap-(--s1)">
            <h3 className="m-0 text-[12px] font-semibold uppercase tracking-[0.06em] text-(--muted)">Batch {trace.batch}: its picking list</h3>
            <List nodes={buildTree(trace.levels)} />
            {trace.notWalked ? (
              <p className="m-0 text-[12px] text-(--warn)">
                {trace.notWalked} in-house order{trace.notWalked === 1 ? '' : 's'} deeper than {trace.depth} levels not walked.
              </p>
            ) : null}
          </div>
          <Freshness t={trace} />
        </>
      ) : null}
    </section>
  );
}
